import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useLocation } from 'react-router-dom'
import { api } from '../lib/api'
import TradeChart   from '../components/TradeChart'
import TradingViewChart from '../components/TradingViewChart'
import FreePreview  from '../components/FreePreview'
import { useAuthStore } from '../hooks/useAuth'

// Parse AI level strings like "$185.20 – $186.50" into a single number (midpoint)
function parseLevelPrice(s?: string): number | null {
  if (!s) return null
  const stripped = s.replace(/\([^)]*\)/g, '').trim()
  const nums = stripped
    .split(/[\s\-\u2013\u2014\/]+/)
    .map(tok => parseFloat(tok.replace(/[^0-9.]/g, '')))
    .filter(n => !isNaN(n) && n > 0)
    .slice(0, 2)
  if (nums.length === 0) return null
  return nums.reduce((a, b) => a + b, 0) / nums.length
}

// Timeframes — intraday locked to Super
const TFS_ALL    = ['1m','5m','15m','1H','4H','1D','1W','1Y']
const TFS_INTRA  = ['1m','5m','15m','1H','4H']
const TF_TO_KEY: Record<string, string> = {
  '1m':'1','5m':'5','15m':'15','1H':'60','4H':'240','1D':'D','1W':'W','1Y':'1Y'
}

// MTF label: what 3 timeframes get fetched for Super
const MTF_LABEL: Record<string, string> = {
  '1m':  '1m + 5m + 15m',
  '5m':  '5m + 15m + 1H',
  '15m': '15m + 1H + 1D',
  '1H':  '1H + 4H + 1D',
  '4H':  '4H + 1D + 1W',
  '1D':  '1D + 1W + Monthly',
  '1W':  'Monthly + Quarterly + 1W',
  '1Y':  '1D + 1W + 1Y',   // daily + weekly give granular structure context for long-term view
}
// Normalize legacy raw keys stored in DB (e.g. 'D' → '1D', '60' → '1H')
const TF_NORMALIZE: Record<string, string> = {
  '1':'1m','5':'5m','15':'15m','60':'1H','240':'4H','D':'1D','W':'1W','M':'1M','3M':'3M','1Y':'1Y'
}
function normalizeTF(tf: string): string {
  return tf.split('+').map(t => TF_NORMALIZE[t.trim()] ?? t.trim()).join(' + ')
}

interface ChartFile { data: string; tf: string; src: string; name: string }


// ─── Watchlist button ─────────────────────────────────────────────────────────
function WatchlistButton({ ticker }: { ticker?: string }) {
  const qc = useQueryClient()
  const [added, setAdded] = useState(false)

  const mutation = useMutation({
    mutationFn: (t: string) => api.watchlist.add(t),
    onSuccess:  () => { setAdded(true); qc.invalidateQueries({ queryKey: ['watchlist'] }) },
  })

  if (!ticker) return null

  return (
    <button
      onClick={() => !added && mutation.mutate(ticker)}
      disabled={added || mutation.isPending}
      style={{
        padding: '4px 12px',
        background: added ? 'var(--stamp)' : 'transparent',
        border: `1px solid ${added ? 'var(--cash-dim)' : 'var(--edge)'}`,
        borderRadius: 2, color: added ? 'var(--cash)' : 'var(--muted)',
        fontFamily: 'Syne Mono', fontSize: '.56rem',
        cursor: added ? 'default' : 'pointer', transition: 'all .15s',
        whiteSpace: 'nowrap',
      }}>
      {added ? '✓ Watchlist' : mutation.isPending ? '...' : '+ Watch'}
    </button>
  )
}

export default function Analyze() {
  const { user } = useAuthStore()
  const navigate  = useNavigate()
  const location  = useLocation()
  const qc        = useQueryClient()
  const canUse    = user?.tier === 'pro' || user?.tier === 'super'
  const isSuper   = user?.tier === 'super'

  // Main tabs
  const [tab,        setTab]        = useState<'analyze'|'history'>('analyze')
  // Input mode: screenshot vs ticker
  const [inputMode,  setInputMode]  = useState<'screenshot'|'ticker'>('screenshot')

  // Prefill from navigation state (e.g. from Scanner "Analyze →" button)
  useEffect(() => {
    const state = location.state as { ticker?: string } | null
    if (state?.ticker) {
      setTickerInput(state.ticker.toUpperCase())
      setInputMode('ticker')
      // Clear state so back-navigation doesn't re-trigger
      window.history.replaceState({}, '')
    }
  }, [])

  // Screenshot state
  const [charts,     setCharts]     = useState<ChartFile[]>([])
  const [dragOver,   setDragOver]   = useState(false)
  const [tfNote,     setTfNote]     = useState<string | null>(null)
  const [detectedTF, setDetectedTF] = useState<string | null>(null)

  // Ticker input state
  const [tickerInput, setTickerInput] = useState('')
  const [selectedTF,  setSelectedTF]  = useState('1D')
  const [includeFundamentals, setIncludeFundamentals] = useState(true)

  // Shared
  const [ticker,    setTicker]    = useState('')
  const [status,    setStatus]    = useState<'idle'|'loading'|'done'|'error'>('idle')
  const [result,    setResult]    = useState<any>(null)
  const [errorMsg,  setErrorMsg]  = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const { data: historyData, isLoading: historyLoading, refetch: refetchHistory } = useQuery({
    queryKey: ['history'],
    queryFn: () => api.charts.history(20),
    enabled: tab === 'history',
    staleTime: 30_000,
  })
  const history = Array.isArray(historyData) ? historyData : []

  const { data: detailData, isLoading: detailLoading, isError: detailError } = useQuery({
    queryKey: ['analysis', expandedId],
    queryFn: () => expandedId ? api.charts.get(expandedId) : null,
    enabled: !!expandedId,
    retry: 1,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.charts.delete(id),
    onSuccess: () => refetchHistory(),
  })

  function readFile(file: File, tf: string, src: string): Promise<ChartFile> {
    return new Promise(res => {
      const reader = new FileReader()
      reader.onload = e => res({ data: e.target!.result as string, tf, src, name: file.name })
      reader.readAsDataURL(file)
    })
  }

  useEffect(() => {
    function handlePaste(e: ClipboardEvent) {
      if (tab !== 'analyze' || inputMode !== 'screenshot') return
      const items = e.clipboardData?.items
      if (!items) return
      const imageFiles: File[] = []
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile()
          if (file) imageFiles.push(file)
        }
      }
      if (imageFiles.length > 0) addFiles(imageFiles)
    }
    window.addEventListener('paste', handlePaste)
    return () => window.removeEventListener('paste', handlePaste)
  }, [tab, inputMode])

  async function addFiles(files: FileList | File[]) {
    const entries = await Promise.all(
      Array.from(files).filter(f => f.type.startsWith('image/')).map(f => readFile(f, selectedTF, 'upload'))
    )
    setCharts(prev => [...prev, ...entries])
  }

  async function runScreenshotAnalysis() {
    if (!canUse) { navigate('/pricing'); return }
    if (!charts.length) return
    setStatus('loading'); setErrorMsg(''); setResult(null); setTfNote(null); setDetectedTF(null)
    try {
      const formData = new FormData()
      formData.append('ticker',      ticker.trim().toUpperCase())
      formData.append('timeframes',  selectedTF)
      formData.append('isRobinhood', 'false')
      for (const chart of charts) {
        const blob = await (await fetch(chart.data)).blob()
        formData.append('charts', blob, chart.name)
      }
      const data = await api.charts.analyze(formData)
      if (data.ticker && data.ticker !== 'UNKNOWN') setTicker(data.ticker)
      const aiTF: string | null = data.detectedTimeframe ?? null
      setDetectedTF(aiTF)
      if (aiTF) {
        const norm = (s: string) => s.toLowerCase().replace(/\s/g, '')
        if (norm(aiTF) !== norm(selectedTF)) {
          setTfNote(`⚠ Timeframe in image appears to be ${aiTF} — you selected ${selectedTF}. Analysis used ${selectedTF}.`)
        } else setTfNote(null)
      } else {
        setTfNote(`ℹ Timeframe not detected — using selected: ${selectedTF}.`)
      }
      setResult({ ...data.analysis, _ticker: data.ticker, _tfs: data.timeframes, _model: data.model, _detectedTF: aiTF, _live: data.liveMarket ?? null, _source: 'screenshot' })
      setStatus('done')
      qc.invalidateQueries({ queryKey: ['history'] })
    } catch (e: any) {
      const msg = e.details ? `${e.message}: ${e.details}` : e.message
      setErrorMsg(msg); setStatus('error')
    }
  }

  async function runTickerAnalysis() {
    if (!canUse) { navigate('/pricing'); return }
    const sym = tickerInput.trim().toUpperCase()
    if (!sym) return
    setStatus('loading'); setErrorMsg(''); setResult(null)
    try {
      const tfKey = TF_TO_KEY[selectedTF] ?? 'D'
      const data  = await api.charts.analyzeTicker(sym, tfKey, includeFundamentals)
      setTicker(sym)
      setResult({ ...data.analysis, _ticker: sym, _tfs: data.timeframes ?? selectedTF, _model: data.model, _live: data.liveMarket ?? null, _barCount: data.barCount, _source: 'ticker', _fallbackUsed: data.fallbackUsed ?? false })
      setStatus('done')
      qc.invalidateQueries({ queryKey: ['history'] })
    } catch (e: any) {
      if (e.upgradeReason === 'intraday') {
        setErrorMsg('Intraday timeframes require Super tier. Switch to Daily/Weekly or upgrade.')
      } else {
        setErrorMsg(e.message)
      }
      setStatus('error')
    }
  }

  const gc  = (result?.grade || '')[0] || ''
  const dc  = (result?.direction || '').replace(/\s+/g, '').toUpperCase()
  const mom = result?.momentum === 'STRONG' ? 'up' : result?.momentum === 'DIVERGING' ? 'dn' : 'mid'

  const isIntraday = TFS_INTRA.includes(selectedTF)

  return (
    <div>
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontSize: '1.6rem', color: 'var(--bill)', letterSpacing: '-0.02em', marginBottom: 4 }}>Chart Analysis</h1>
          <div className="eyebrow">
            {user?.tier === 'super' ? 'Super AI' : user?.tier === 'pro' ? 'Pro AI' : 'Analysis'}
            {canUse ? ` · ${user?.analysesThisMonth ?? 0} analyses this month` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', border: '1px solid var(--edge)', borderRadius: 3, overflow: 'hidden' }}>
          {(['analyze', 'history'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '7px 18px', background: tab === t ? 'var(--stamp)' : 'transparent',
              border: 'none', borderBottom: tab === t ? '2px solid var(--cash)' : '2px solid transparent',
              color: tab === t ? 'var(--cash)' : 'var(--muted)',
              fontFamily: 'Syne', fontWeight: 600, fontSize: '0.72rem', cursor: 'pointer',
            }}><span style={{ display:'flex', alignItems:'center', gap:5 }}>{t === 'analyze' ? <><img src="/icons/analyze.svg" alt="" style={{width:13,height:13,verticalAlign:'middle'}}/> Analyze</> : <><img src="/icons/history.svg" alt="" style={{width:13,height:13,verticalAlign:'middle'}}/> History</>}</span></button>
          ))}
        </div>
      </div>

      {tab === 'analyze' && (
        <>
        <div className="analyze-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
          {/* ── Left panel ── */}
          <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--edge)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              {/* Input mode toggle */}
              <div style={{ display: 'flex', gap: 0, border: '1px solid var(--edge)', borderRadius: 3, overflow: 'hidden' }}>
                {([['screenshot', 'screenshot'], ['ticker', 'ticker_input']] as const).map(([mode, iconKey]) => (
                  <button key={mode} onClick={() => { if (canUse) setInputMode(mode) }} style={{
                    padding: '5px 12px',
                    background: inputMode === mode ? 'var(--stamp)' : 'transparent',
                    border: 'none',
                    borderBottom: inputMode === mode ? '2px solid var(--cash)' : '2px solid transparent',
                    color: inputMode === mode ? 'var(--cash)' : 'var(--muted)',
                    fontFamily: 'Syne Mono', fontSize: '.6rem', cursor: canUse ? 'pointer' : 'default',
                  }}><span style={{display:'flex',alignItems:'center',gap:4}}><img src={`/icons/${iconKey}.svg`} alt="" style={{width:12,height:12,verticalAlign:'middle'}}/>{mode === 'screenshot' ? 'Screenshot' : 'Ticker Input'}</span></button>
                ))}
              </div>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: status === 'loading' ? 'var(--amber)' : 'var(--cash)', boxShadow: '0 0 8px var(--cash)' }} />
            </div>

            <div style={{ padding: '18px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* ── Screenshot mode ── */}
              {inputMode === 'screenshot' && (
                <>
                  <div
                    onDragOver={e => { e.preventDefault(); setDragOver(true) }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files) }}
                    onClick={() => document.getElementById('chart-file-input')?.click()}
                    style={{
                      border: `2px dashed ${dragOver ? 'var(--cash)' : charts.length ? 'var(--cash-dim)' : 'var(--edge)'}`,
                      borderRadius: 4, padding: charts.length ? '12px' : '28px 16px',
                      textAlign: 'center', cursor: 'pointer',
                      background: dragOver ? 'rgba(133,196,124,.04)' : 'transparent',
                      transition: 'all .15s',
                    }}
                  >
                    <input id="chart-file-input" type="file" accept="image/*" multiple
                      onChange={e => { addFiles(e.target.files!); (e.target as HTMLInputElement).value = '' }}
                      style={{ display: 'none' }} />
                    {charts.length === 0 ? (
                      <>
                        <img src="/icons/analyze.svg" alt="" style={{width:36,height:36,opacity:.25,marginBottom:8}}/>
                        <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '.82rem', color: 'var(--muted)', marginBottom: 4 }}>
                          Drop charts here or tap to upload
                        </div>
                        <div style={{ fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)', opacity: .5 }}>
                          Ctrl+V · drag & drop · any platform
                        </div>
                      </>
                    ) : (
                      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', justifyContent: 'center' }}>
                        {charts.map((ch, i) => (
                          <div key={i} style={{ position: 'relative', width: 72, height: 56, border: '1px solid var(--edge)', borderRadius: 3, overflow: 'hidden', flexShrink: 0 }}>
                            <img src={ch.data} alt="chart" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            <button onClick={e => { e.stopPropagation(); setCharts(prev => prev.filter((_, idx) => idx !== i)) }}
                              style={{ position: 'absolute', top: 2, right: 2, width: 14, height: 14, background: 'var(--red)', border: 'none', borderRadius: 1, color: '#fff', fontSize: '.5rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                          </div>
                        ))}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 72, height: 56, border: '1px dashed var(--edge)', borderRadius: 3, opacity: .4 }}>
                          <span style={{ fontSize: '.6rem', color: 'var(--muted)' }}>+ add</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Timeframe */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                      <div className="eyebrow">Timeframe</div>
                      {detectedTF && (
                        <div style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--cash)', padding: '2px 6px', border: '1px solid var(--cash-dim)', borderRadius: 2, background: 'var(--stamp)' }}>
                          detected TF: {detectedTF}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      {TFS_ALL.map(tf => (
                        <button key={tf} onClick={() => setSelectedTF(tf)} style={{
                          padding: '5px 11px', background: selectedTF === tf ? 'var(--stamp)' : 'transparent',
                          border: `1px solid ${selectedTF === tf ? 'var(--cash-dim)' : 'var(--edge)'}`,
                          color: selectedTF === tf ? 'var(--cash)' : 'var(--muted)',
                          fontFamily: 'Syne Mono', fontSize: '.68rem', borderRadius: 2, cursor: 'pointer',
                        }}>{tf}</button>
                      ))}
                    </div>
                    {tfNote && (
                      <div style={{
                        marginTop: 8, fontFamily: 'Syne Mono', fontSize: '.58rem', lineHeight: 1.5,
                        color: tfNote.startsWith('⚠') ? 'var(--amber)' : 'var(--muted)',
                        background: tfNote.startsWith('⚠') ? 'rgba(212,168,67,.06)' : 'transparent',
                        border: tfNote.startsWith('⚠') ? '1px solid rgba(212,168,67,.2)' : 'none',
                        borderRadius: 2, padding: tfNote.startsWith('⚠') ? '6px 9px' : '0',
                      }}>{tfNote}</div>
                    )}
                    {/* Workflow confirmation */}
                    <div style={{ marginTop: 10, background: 'rgba(133,196,124,.04)', border: '1px solid var(--cash-dim)', borderRadius: 2, padding: '8px 12px' }}>
                      <div style={{ fontFamily: 'Syne Mono', fontSize: '.5rem', letterSpacing: '.15em', textTransform: 'uppercase', color: 'var(--cash)', marginBottom: 6 }}>
                        Analysis pipeline · {selectedTF}
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {['Upload chart', 'Detect ticker & TF', `Analyze ${selectedTF}`, 'ICT scoring', 'Grade setup'].map((step, i) => (
                          <div key={step} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <div style={{ fontFamily: 'Syne Mono', fontSize: '.54rem', color: 'var(--muted)', background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 2, padding: '2px 7px', whiteSpace: 'nowrap' }}>
                              {step}
                            </div>
                            {i < 4 && <div style={{ color: 'var(--edge2)', fontSize: '.6rem' }}>›</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Ticker + Analyze */}
                  <div>
                    <div className="eyebrow" style={{ marginBottom: 8 }}>Ticker Symbol</div>
                    <div style={{ display: 'flex', gap: 7 }}>
                      <input value={ticker} onChange={e => setTicker(e.target.value.toUpperCase())}
                        placeholder="auto-detected" maxLength={10}
                        style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '9px 13px', color: 'var(--text)', fontFamily: 'Syne Mono', fontSize: '.82rem', outline: 'none', width: 120 }} />
                      <button onClick={runScreenshotAnalysis} disabled={!charts.length || status === 'loading'} style={{
                        flex: 1, padding: '9px 20px', background: 'var(--cash)', color: 'var(--ink)', border: 'none', borderRadius: 2,
                        fontFamily: 'Syne', fontWeight: 800, fontSize: '.75rem', letterSpacing: '.08em', textTransform: 'uppercase',
                        cursor: charts.length && status !== 'loading' ? 'pointer' : 'not-allowed',
                        opacity: !charts.length || status === 'loading' ? .35 : 1, whiteSpace: 'nowrap',
                      }}>{status === 'loading' ? 'Analyzing...' : `Analyze ${selectedTF} →`}</button>
                    </div>
                  </div>
                </>
              )}

              {/* ── Ticker input mode ── */}
              {inputMode === 'ticker' && (
                <>
                  {/* Info banner */}
                  <div style={{ background: 'rgba(133,196,124,.04)', border: '1px solid var(--cash-dim)', borderRadius: 3, padding: '10px 13px', fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', lineHeight: 1.6 }}>
                    <span style={{ color: 'var(--cash)', fontWeight: 700 }}>Live data</span> — no screenshot needed. Polywally fetches real OHLCV bars and runs full ICT analysis on actual price data.
                    {isSuper && <span style={{ color: 'var(--amber)' }}> Super: 3-timeframe MTF analysis included.</span>}
                  </div>

                  {/* Ticker */}
                  <div>
                    <div className="eyebrow" style={{ marginBottom: 8 }}>Ticker Symbol</div>
                    <input
                      value={tickerInput}
                      onChange={e => setTickerInput(e.target.value.toUpperCase())}
                      onKeyDown={e => e.key === 'Enter' && runTickerAnalysis()}
                      placeholder="AAPL, SPY, NVDA..."
                      maxLength={10}
                      style={{ width: '100%', background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '10px 13px', color: 'var(--text)', fontFamily: 'Syne Mono', fontSize: '.9rem', outline: 'none', letterSpacing: '.05em', boxSizing: 'border-box' }}
                    />
                  </div>

                  {/* Timeframe — intraday locked for Pro */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                      <div className="eyebrow">Timeframe</div>
                      {isIntraday && !isSuper && (
                        <div style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--amber)', padding: '2px 7px', border: '1px solid rgba(212,168,67,.3)', borderRadius: 2, background: 'rgba(212,168,67,.06)' }}>
                          <span style={{display:"flex",alignItems:"center",gap:4}}><img src="/icons/lock.svg" alt="" style={{width:11,height:11}}/> Super only</span>
                        </div>
                      )}
                      {isSuper ? (
                        <div style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--cash)', padding: '2px 7px', border: '1px solid var(--cash-dim)', borderRadius: 2, background: 'var(--stamp)' }}>
                          MTF: {MTF_LABEL[selectedTF] ?? selectedTF}
                        </div>
                      ) : !isIntraday ? (
                        <div style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--muted)', padding: '2px 7px', border: '1px solid var(--edge)', borderRadius: 2 }}>
                          {selectedTF} · {selectedTF === '1D' ? '100 daily bars' : selectedTF === '1Y' ? '60 monthly bars (~5yr)' : '52 weekly bars'}
                        </div>
                      ) : null}
                    </div>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      {TFS_ALL.map(tf => {
                        const locked = TFS_INTRA.includes(tf) && !isSuper
                        return (
                          <button key={tf} onClick={() => !locked && setSelectedTF(tf)} style={{
                            padding: '5px 11px',
                            background: selectedTF === tf ? 'var(--stamp)' : 'transparent',
                            border: `1px solid ${selectedTF === tf ? 'var(--cash-dim)' : locked ? 'rgba(212,168,67,.2)' : 'var(--edge)'}`,
                            color: selectedTF === tf ? 'var(--cash)' : locked ? 'rgba(212,168,67,.5)' : 'var(--muted)',
                            fontFamily: 'Syne Mono', fontSize: '.68rem', borderRadius: 2,
                            cursor: locked ? 'not-allowed' : 'pointer',
                            position: 'relative',
                          }}>
                            {tf}{locked ? ' ·🔒' : ''}
                          </button>
                        )
                      })}
                    </div>
                    {isIntraday && !isSuper && (
                      <div style={{ marginTop: 8, fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--amber)', lineHeight: 1.5 }}>
                        Intraday timeframes require Super tier.{' '}
                        <span onClick={() => navigate('/pricing')} style={{ color: 'var(--cash)', cursor: 'pointer', textDecoration: 'underline' }}>Upgrade →</span>
                      </div>
                    )}
                    {/* Workflow confirmation */}
                    {!(isIntraday && !isSuper) && (
                      <div style={{ marginTop: 10, background: 'rgba(133,196,124,.04)', border: '1px solid var(--cash-dim)', borderRadius: 2, padding: '8px 12px' }}>
                        <div style={{ fontFamily: 'Syne Mono', fontSize: '.5rem', letterSpacing: '.15em', textTransform: 'uppercase', color: 'var(--cash)', marginBottom: 6 }}>
                          Analysis pipeline · {isSuper ? MTF_LABEL[selectedTF] ?? selectedTF : selectedTF}
                        </div>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {(isSuper
                            ? ['Fetch live bars', `${selectedTF} structure`, 'MTF confluence', 'ICT scoring', 'Grade setup']
                            : ['Fetch live bars', `${selectedTF} structure`, 'Order blocks & FVGs', 'ICT scoring', 'Grade setup']
                          ).map((step, i, arr) => (
                            <div key={step} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <div style={{ fontFamily: 'Syne Mono', fontSize: '.54rem', color: 'var(--muted)', background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 2, padding: '2px 7px', whiteSpace: 'nowrap' }}>
                                {step}
                              </div>
                              {i < arr.length - 1 && <div style={{ color: 'var(--edge2)', fontSize: '.6rem' }}>›</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Fundamentals toggle */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none' }}>
                      <input
                        type="checkbox"
                        checked={includeFundamentals}
                        onChange={e => setIncludeFundamentals(e.target.checked)}
                        style={{ accentColor: 'var(--cash)', width: 14, height: 14, cursor: 'pointer' }}
                      />
                      <span style={{ fontFamily: 'Syne Mono', fontSize: '.62rem', color: includeFundamentals ? 'var(--cash)' : 'var(--muted)' }}>
                        Include fundamentals
                      </span>
                    </label>
                    <span style={{ fontFamily: 'Syne Mono', fontSize: '.48rem', color: 'var(--muted)', opacity: .6 }}>
                      P/E, analyst ratings, insider activity
                    </span>
                  </div>

                  {/* Analyze button */}
                  <button
                    onClick={runTickerAnalysis}
                    disabled={!tickerInput.trim() || status === 'loading' || (isIntraday && !isSuper)}
                    style={{
                      width: '100%', padding: '11px', background: 'var(--cash)', color: 'var(--ink)',
                      border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.78rem',
                      letterSpacing: '.08em', textTransform: 'uppercase',
                      cursor: tickerInput.trim() && status !== 'loading' && !(isIntraday && !isSuper) ? 'pointer' : 'not-allowed',
                      opacity: !tickerInput.trim() || status === 'loading' || (isIntraday && !isSuper) ? .35 : 1,
                    }}>
                    {status === 'loading' ? 'Fetching data...' : `Analyze ${tickerInput || 'Ticker'} ${selectedTF} →`}
                  </button>

                  {!isSuper && (
                    <div style={{ background: 'rgba(212,168,67,.04)', border: '1px solid rgba(212,168,67,.2)', borderRadius: 3, padding: '10px 13px' }}>
                      <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.72rem', color: 'var(--amber)', marginBottom: 4 }}>Super unlocks intraday MTF</div>
                      <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)', lineHeight: 1.6 }}>
                        Pro: Daily & Weekly analysis from live data.<br />
                        Super: all timeframes with automatic 3-TF MTF confluence (e.g. 1m + 5m + 15m, 1H + 4H + 1D).
                      </div>
                      <button onClick={() => navigate('/pricing')} style={{ marginTop: 8, padding: '6px 14px', background: 'transparent', border: '1px solid var(--cash)', borderRadius: 2, color: 'var(--cash)', fontFamily: 'Syne', fontWeight: 700, fontSize: '.65rem', cursor: 'pointer' }}>
                        Upgrade to Super →
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* ── Right panel: output ── */}
          <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--edge)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span className="eyebrow">Analysis Output</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {result?._source === 'ticker' && (
                  <span style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--cash)', padding: '2px 7px', border: '1px solid var(--cash-dim)', borderRadius: 2, background: 'var(--stamp)' }}>
                    <img src="/icons/trading.svg" alt="" style={{width:11,height:11,verticalAlign:'middle',marginRight:3}}/> Live data · {result._barCount} bars
                  </span>
                )}
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: status === 'done' ? 'var(--cash)' : status === 'loading' ? 'var(--amber)' : 'var(--muted)' }} />
              </div>
            </div>
            <div style={{ padding: '18px 16px', flex: 1, display: 'flex', flexDirection: 'column', gap: 14, minHeight: 460 }}>
              {status === 'idle' && (
                !canUse
                  ? <FreePreview type="chart" onUpgrade={() => navigate('/pricing')} />
                  : <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: .2 }}>
                      <img src="/icons/trading.svg" alt="" style={{width:48,height:48,opacity:.35,marginBottom:8}}/>
                      <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', letterSpacing: '.15em', textTransform: 'uppercase' }}>
                        {inputMode === 'ticker' ? 'Enter a ticker to begin' : 'Upload a chart to begin'}
                      </div>
                    </div>
              )}
              {status === 'loading' && (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18 }}>
                  <div style={{ width: 160, height: 2, background: 'var(--edge)', borderRadius: 1, overflow: 'hidden', position: 'relative' }}>
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg,transparent,var(--cash),transparent)', animation: 'sweep 1.4s ease-in-out infinite' }} />
                  </div>
                  <style>{`@keyframes sweep{0%{transform:translateX(-200%)}100%{transform:translateX(400%)}}`}</style>
                  {(inputMode === 'ticker'
                    ? ['Fetching live market data','Identifying order blocks & FVGs','Analyzing market structure','Computing premium/discount zones', isSuper ? 'Building MTF confluence' : 'Grading setup quality']
                    : ['Processing images','Detecting market structure','Reading order blocks & FVGs','Analyzing price action','Grading setup quality']
                  ).map((s, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--muted)' }}>
                      <div style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--muted)', flexShrink: 0 }} />{s}
                    </div>
                  ))}
                </div>
              )}
              {status === 'error' && (
                <div style={{ background: 'rgba(224,92,92,.06)', border: '1px solid rgba(224,92,92,.25)', borderRadius: 3, padding: 14 }}>
                  <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '.8rem', color: 'var(--red)', marginBottom: 6, display:'flex', alignItems:'center', gap:6 }}><img src="/icons/warning.svg" alt="" style={{width:14,height:14}}/> Analysis failed</div>
                  <div style={{ fontFamily: 'Syne Mono', fontSize: '.68rem', color: 'var(--red)', lineHeight: 1.6 }}>{errorMsg}</div>
                </div>
              )}
              {status === 'done' && result && (
                <>
                  {result._live && (
                    <div style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 3, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--muted)', letterSpacing: '.1em' }}>LIVE · {result._live.source}</div>
                      <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1rem', color: result._live.change >= 0 ? 'var(--cash)' : 'var(--red)' }}>${result._live.price?.toFixed(2)}</div>
                      <div style={{ fontFamily: 'Syne Mono', fontSize: '.62rem', color: result._live.change >= 0 ? 'var(--cash)' : 'var(--red)' }}>
                        {result._live.change >= 0 ? '+' : ''}{result._live.change?.toFixed(2)} ({result._live.changePct})
                      </div>
                      <div style={{ display: 'flex', gap: 10, marginLeft: 'auto', fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)' }}>
                        <span>H: ${result._live.high?.toFixed(2)}</span>
                        <span>L: ${result._live.low?.toFixed(2)}</span>
                        {result._live.volume && <span>Vol: {result._live.volume.toLocaleString()}</span>}
                      </div>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '3.8rem', lineHeight: 1, color: gc === 'A' ? 'var(--cash)' : gc === 'B' ? '#a8d8a0' : gc === 'C' ? 'var(--amber)' : gc === 'D' ? '#d48843' : 'var(--red)' }}>{result.grade}</div>
                    <div style={{ padding: '6px 14px', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '1rem', letterSpacing: '.06em', textTransform: 'uppercase', ...(dc === 'LONG' ? { background: 'rgba(133,196,124,.12)', color: 'var(--cash)', border: '1px solid var(--cash)' } : dc === 'SHORT' ? { background: 'rgba(224,92,92,.12)', color: 'var(--red)', border: '1px solid var(--red)' } : { background: 'rgba(212,168,67,.12)', color: 'var(--amber)', border: '1px solid var(--amber)' }) }}>{result.direction}</div>
                    <div style={{ marginLeft: 'auto', fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--muted)' }}>{result._ticker} · {result.confidence ?? '?'}%</div>
                    <WatchlistButton ticker={result._ticker} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
                    {(() => {
                      // Momentum gets a directional qualifier based on trend
                      const momDir = result.trend === 'BEARISH' ? ' ↓' : result.trend === 'BULLISH' ? ' ↑' : ''
                      const momVal = result.momentum ? result.momentum + momDir : null
                      return [
                        { label: 'Entry Zone',       val: result.entry_zone,               cls: '' },
                        { label: 'Stop Loss',         val: result.stop_loss,                cls: 'dn' },
                        { label: 'ATR Stop',          val: result.stop_loss_atr,            cls: 'dn' },
                        { label: 'Target 1',          val: result.target_1,                 cls: 'up' },
                        { label: 'Target 2',          val: result.target_2,                 cls: 'up' },
                        { label: 'R:R',               val: result.risk_reward,              cls: '' },
                        { label: 'R:R Conservative',  val: result.risk_reward_conservative, cls: '' },
                        { label: 'Setup Quality',     val: result.setup_quality,            cls: '' },
                        { label: 'Trade Readiness',   val: result.trade_readiness,          cls: result.trade_readiness === 'READY' ? 'up' : result.trade_readiness === 'NO TRADE' ? 'dn' : 'mid' },
                        { label: 'Momentum',          val: momVal,                          cls: mom },
                      ]
                    })().filter(r => r.val && r.val !== 'N/A' && r.val !== 'n/a').map(({ label, val, cls }) => (
                      <div key={label} style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 3, padding: '8px 11px' }}>
                        <div style={{ fontFamily: 'Syne Mono', fontSize: '.54rem', letterSpacing: '.18em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 2 }}>{label}</div>
                        <div style={{ fontFamily: 'Syne Mono', fontSize: '.75rem', color: cls === 'up' ? 'var(--cash)' : cls === 'dn' ? 'var(--red)' : cls === 'mid' ? 'var(--amber)' : 'var(--text)' }}>{val}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ background: 'rgba(133,196,124,.04)', border: '1px solid var(--cash-dim)', borderLeft: '3px solid var(--cash)', borderRadius: 2, padding: '11px 13px' }}>
                    <p style={{ fontSize: '.76rem', lineHeight: 1.7, opacity: .9 }}>{result.summary}</p>
                    {result.fundamental_context && (
                      <div style={{ marginTop: 8, padding: '8px 11px', background: 'rgba(212,168,67,.05)', border: '1px solid rgba(212,168,67,.2)', borderRadius: 2 }}>
                        <div style={{ fontFamily: 'Syne Mono', fontSize: '.48rem', letterSpacing: '.12em', color: 'var(--amber)', marginBottom: 4 }}>FUNDAMENTAL CONTEXT</div>
                        <p style={{ fontSize: '.68rem', lineHeight: 1.6, color: 'var(--bill)' }}>{result.fundamental_context}</p>
                      </div>
                    )}
                    {result.beginner_explanation && <p style={{ marginTop: 7, fontSize: '.68rem', color: 'var(--muted)', fontStyle: 'italic' }}>{result.beginner_explanation}</p>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)' }}>
                    <div style={{ width: 4, height: 4, borderRadius: '50%', background: (isSuper && result._fallbackUsed) ? 'var(--amber)' : 'var(--cash)' }} />
                    {isSuper && result._fallbackUsed
                      ? <span>Pro AI <span style={{ color: 'var(--amber)' }}>· Claude unavailable</span></span>
                      : <span>{result._model === 'claude-haiku' ? 'Super AI' : 'Pro AI'}</span>
                    } · {result._source === 'ticker' ? selectedTF : result._tfs}
                    {result._source === 'ticker' ? ' · Live data' : result._detectedTF ? ` · detected ${result._detectedTF}` : ''}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Full-width TradingView chart — below the input/output grid */}
        {((inputMode === 'ticker' && tickerInput.trim().length >= 1) || result?._ticker) && (
          <div style={{ marginTop: 18 }}>
            <TradingViewChart
              ticker={result?._ticker || tickerInput.trim()}
              interval={selectedTF}
              height={540}
              entry={result ? parseLevelPrice(result.entry_zone) : undefined}
              stopLoss={result ? parseLevelPrice(result.stop_loss) : undefined}
              target1={result ? parseLevelPrice(result.target_1) : undefined}
              target2={result ? parseLevelPrice(result.target_2) : undefined}
              direction={result?.direction}
            />
          </div>
        )}
        </>
      )}

      {tab === 'history' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {historyLoading && (
            <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 3, padding: 32, textAlign: 'center', fontFamily: 'Syne Mono', fontSize: '.68rem', color: 'var(--muted)' }}>
              Loading...
            </div>
          )}
          {!historyLoading && history.length === 0 && (
            <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 3, padding: 32, textAlign: 'center', fontFamily: 'Syne Mono', fontSize: '.68rem', color: 'var(--muted)' }}>
              No analyses yet — run your first one
            </div>
          )}
          {history.map((h: any) => {
            const isOpen = expandedId === h.id
            const detail = isOpen ? detailData : null
            const gc = (h.grade || '')[0] || ''
            const gradeColor = gc === 'A' ? 'var(--cash)' : gc === 'B' ? '#a8d8a0' : gc === 'C' ? 'var(--amber)' : gc === 'D' ? '#d48843' : 'var(--red)'
            return (
              <div key={h.id} style={{ background: 'var(--card)', border: `1px solid ${isOpen ? 'var(--cash-dim)' : 'var(--edge)'}`, borderRadius: 3, overflow: 'hidden' }}>
                <div onClick={() => setExpandedId(isOpen ? null : h.id)}
                  style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', userSelect: 'none' }}>
                  <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1.5rem', color: gradeColor, width: 40, flexShrink: 0 }}>{h.grade}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, flexWrap: 'wrap' }}>
                      <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '0.9rem' }}>{h.ticker}</div>
                      <div style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--cash)', background: 'var(--stamp)', border: '1px solid var(--cash-dim)', borderRadius: 2, padding: '2px 7px', letterSpacing: '.08em' }}>{normalizeTF(h.timeframes ?? '')}</div>
                    </div>
                    <div style={{ fontFamily: 'Syne Mono', fontSize: '0.6rem', color: 'var(--muted)' }}>{h.direction} · {h.confidence}% confidence</div>
                  </div>
                  <div style={{ fontFamily: 'Syne Mono', fontSize: '0.58rem', color: 'var(--muted)', textAlign: 'right', flexShrink: 0, marginRight: 6 }}>
                    <div style={{ padding: '2px 7px', border: '1px solid var(--edge)', borderRadius: 2, marginBottom: 3 }}>{h.aiModel === 'claude-haiku' ? 'Super AI' : 'Pro AI'}</div>
                    <div>{new Date(h.createdAt * 1000).toLocaleDateString()}</div>
                  </div>
                  <div style={{ color: 'var(--muted)', fontSize: '0.65rem', flexShrink: 0 }}>{isOpen ? '▲' : '▼'}</div>
                </div>
                {isOpen && (
                  <div style={{ borderTop: '1px solid var(--edge)', padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {(detailLoading || (!detail && !detailError))
                      ? <div style={{ fontFamily: 'Syne Mono', fontSize: '0.65rem', color: 'var(--muted)', textAlign: 'center', padding: '16px 0' }}>Loading...</div>
                      : (detailError || detail?.error)
                        ? <div style={{ fontFamily: 'Syne Mono', fontSize: '0.65rem', color: 'var(--red)' }}>Failed to load — try closing and reopening</div>
                      : <>
                          {detail.ticker && (
                            <TradingViewChart
                              ticker={detail.ticker}
                              interval={normalizeTF(h.timeframes ?? '1D')}
                              height={360}
                              entry={parseLevelPrice(detail.entryZone)}
                              stopLoss={parseLevelPrice(detail.stopLoss)}
                              target1={parseLevelPrice(detail.target1)}
                              target2={parseLevelPrice(detail.target2)}
                              direction={detail.direction}
                            />
                          )}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px,1fr))', gap: 7 }}>
                            {[{ label: 'Entry Zone', val: detail.entryZone },{ label: 'Stop Loss', val: detail.stopLoss },{ label: 'Target 1', val: detail.target1 },{ label: 'Target 2', val: detail.target2 },{ label: 'Risk/Reward', val: detail.riskReward },{ label: 'Momentum', val: detail.momentum }].filter(r => r.val).map(({ label, val }) => (
                              <div key={label} style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '7px 10px' }}>
                                <div style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', letterSpacing: '.15em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 2 }}>{label}</div>
                                <div style={{ fontFamily: 'Syne Mono', fontSize: '.72rem' }}>{val}</div>
                              </div>
                            ))}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                            {([{ key: 'marketStructure', label: 'Market Structure' },{ key: 'orderBlocks', label: 'Order Blocks' },{ key: 'fairValueGaps', label: 'Fair Value Gaps' },{ key: 'liquidity', label: 'Liquidity' },{ key: 'bosChoch', label: 'BOS / CHoCH' },{ key: 'premiumDiscount', label: 'Premium & Discount' },{ key: 'ote', label: 'Optimal Trade Entry' },{ key: 'keyLevels', label: 'Key Levels' },{ key: 'mtfConfluence', label: 'MTF Confluence' },{ key: 'displacement', label: 'Displacement' }] as {key: string; label: string}[]).filter(m => (detail as any)[m.key]).map(m => (
                              <div key={m.key} style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '7px 10px', fontSize: '.7rem', lineHeight: 1.6 }}>
                                <strong style={{ color: 'var(--cash)', fontFamily: 'Syne Mono', fontSize: '.58rem', letterSpacing: '.05em' }}>{m.label}: </strong>
                                {(detail as any)[m.key]}
                              </div>
                            ))}
                          </div>
                          {detail.summary && (
                            <div style={{ background: 'rgba(133,196,124,.04)', border: '1px solid var(--cash-dim)', borderLeft: '3px solid var(--cash)', borderRadius: 2, padding: '11px 13px' }}>
                              <p style={{ fontSize: '.74rem', lineHeight: 1.7 }}>{detail.summary}</p>
                              {(() => {
                                try {
                                  const raw = detail.rawResponse ? JSON.parse(detail.rawResponse) : null
                                  if (raw?.fundamental_context) return (
                                    <div style={{ marginTop: 8, padding: '8px 11px', background: 'rgba(212,168,67,.05)', border: '1px solid rgba(212,168,67,.2)', borderRadius: 2 }}>
                                      <div style={{ fontFamily: 'Syne Mono', fontSize: '.48rem', letterSpacing: '.12em', color: 'var(--amber)', marginBottom: 4 }}>FUNDAMENTAL CONTEXT</div>
                                      <p style={{ fontSize: '.68rem', lineHeight: 1.6, color: 'var(--bill)' }}>{raw.fundamental_context}</p>
                                    </div>
                                  )
                                } catch {} return null
                              })()}
                              {detail.beginnerExplanation && <p style={{ marginTop: 6, fontSize: '.66rem', color: 'var(--muted)', fontStyle: 'italic' }}>{detail.beginnerExplanation}</p>}
                            </div>
                          )}
                        </>
                    }
                    <button
                      onClick={e => { e.stopPropagation(); if (window.confirm('Delete this analysis?')) { deleteMutation.mutate(h.id); setExpandedId(null) } }}
                      style={{ background: 'rgba(224,92,92,.08)', border: '1px solid rgba(224,92,92,.25)', borderRadius: 2, color: 'var(--red)', fontFamily: 'Syne Mono', fontSize: '.65rem', padding: '9px', cursor: 'pointer', width: '100%' }}>
                      Delete this analysis
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
